/* eslint-disable no-restricted-syntax */
import {
  ProposalResiduePlanCodeEnum,
  ProposalResiduePlanQuotationType,
  ProposalResiduePlanType,
  ProposalResidueType,
  ProposalServiceQuotationType,
  ProposalServiceType,
  ProposalTipoPrecificacaoEnum,
  ProposalType,
} from '../../models/core/proposals.type';
import { hasCompletePlanDefinition } from './residue-plan';

type ValidationResultType = { isBusinessException: boolean; message: string; list: string[] } | null;

const hasNumber = (value: unknown) => typeof value === 'number' && !Number.isNaN(value);

const isSpecificPricing = (tipoPrecificacao?: ProposalTipoPrecificacaoEnum | null) =>
  tipoPrecificacao === ProposalTipoPrecificacaoEnum.Especifica;

const hasGeneralPricing = (item: { preco: number | null; imposto: number | null }) =>
  hasNumber(item.preco) && hasNumber(item.imposto);

const hasPlanPricing = (item: {
  precificacao?: {
    tipo?: ProposalTipoPrecificacaoEnum | null;
    preco: number | null;
    imposto: number | null;
  } | null;
}) => {
  if (!item.precificacao || isSpecificPricing(item.precificacao.tipo)) {
    return true;
  }

  return hasGeneralPricing(item.precificacao);
};

const hasCustomerPricing = (item: {
  precificacao?: {
    preco?: number | null;
    imposto?: number | null;
  } | null;
}) => hasNumber(item.precificacao?.preco) && hasNumber(item.precificacao?.imposto);

const hasQuotationCustomerPricing = (
  item: {
    precificacao?: {
      preco?: number | null;
      imposto?: number | null;
    } | null;
  },
  tipoPrecificacao?: ProposalTipoPrecificacaoEnum | null
) => {
  if (isSpecificPricing(tipoPrecificacao)) {
    return hasCustomerPricing(item);
  }

  return true;
};

const validateResidueQuotationItemPricing = <
  T extends {
    precificacao?: {
      preco?: number | null;
      imposto?: number | null;
    } | null;
  }
>(
  item: T,
  tipoPrecificacao?: ProposalTipoPrecificacaoEnum | null
) => hasQuotationCustomerPricing(item, tipoPrecificacao);

const packagingPlanItems = (plan: ProposalResiduePlanType) => plan.acondicionamentos;
const equipmentPlanItems = (plan: ProposalResiduePlanType) => plan.equipamentos;
const vehiclePlanItems = (plan: ProposalResiduePlanType) => (plan.veiculo ? [plan.veiculo] : []);
const treatmentPlanItems = (plan: ProposalResiduePlanType) => (plan.tratamento ? [plan.tratamento] : []);
const destinationPlanItems = (plan: ProposalResiduePlanType) =>
  plan.destinoFinal ? [plan.destinoFinal] : [];

const packagingQuotationItems = (quotation: ProposalResiduePlanQuotationType) => quotation.acondicionamentos;
const equipmentQuotationItems = (quotation: ProposalResiduePlanQuotationType) => quotation.equipamentos;
const vehicleQuotationItems = (quotation: ProposalResiduePlanQuotationType) =>
  quotation.veiculo ? [quotation.veiculo] : [];
const treatmentQuotationItems = (quotation: ProposalResiduePlanQuotationType) =>
  quotation.tratamento ? [quotation.tratamento] : [];
const destinationQuotationItems = (quotation: ProposalResiduePlanQuotationType) =>
  quotation.destinoFinal ? [quotation.destinoFinal] : [];

const validatePlanQuotationReference = <
  TPlanItem extends Record<string, unknown>,
  TQuotationItem extends Record<string, unknown>
>(
  planItems: TPlanItem[],
  quotationItems: TQuotationItem[],
  getPlanKey: (item: TPlanItem) => string | number | null | undefined,
  getQuotationKey: (item: TQuotationItem) => string | number | null | undefined
) => {
  if (planItems.length !== quotationItems.length) {
    return false;
  }

  const planKeys = planItems.map(getPlanKey).filter((value) => value !== null && value !== undefined);
  const quotationKeys = quotationItems
    .map(getQuotationKey)
    .filter((value) => value !== null && value !== undefined);

  if (planKeys.length !== planItems.length || quotationKeys.length !== quotationItems.length) {
    return false;
  }

  return planItems.every((planItem) =>
    quotationItems.some((quotationItem) => `${getPlanKey(planItem)}` === `${getQuotationKey(quotationItem)}`)
  );
};

const buildPackagingMatcher = (item: { idPropostaResiduoPlanoAcondicionamento?: number | null }) =>
  item.idPropostaResiduoPlanoAcondicionamento;

const buildEquipmentMatcher = (item: { idPropostaResiduoPlanoEquipamento?: number | null }) =>
  item.idPropostaResiduoPlanoEquipamento;

const buildVehicleMatcher = (item: { idPropostaResiduoPlanoVeiculo?: number | null }) =>
  item.idPropostaResiduoPlanoVeiculo;

const buildTreatmentMatcher = (item: { idPropostaResiduoPlanoTratamento?: number | null }) =>
  item.idPropostaResiduoPlanoTratamento;

const buildDestinationMatcher = (item: { idPropostaResiduoPlanoDestinoFinal?: number | null }) =>
  item.idPropostaResiduoPlanoDestinoFinal;

const validateResidueQuotationReferences = (
  residuo: ProposalResidueType,
  plan: ProposalResiduePlanType,
  quotation: ProposalResiduePlanQuotationType
) => {
  const invalidSections: string[] = [];

  if (
    !validatePlanQuotationReference(
      packagingPlanItems(plan),
      packagingQuotationItems(quotation),
      buildPackagingMatcher,
      buildPackagingMatcher
    )
  ) {
    invalidSections.push('Acondicionamentos');
  }

  if (
    !validatePlanQuotationReference(
      equipmentPlanItems(plan),
      equipmentQuotationItems(quotation),
      buildEquipmentMatcher,
      buildEquipmentMatcher
    )
  ) {
    invalidSections.push('Equipamentos');
  }

  if (
    !validatePlanQuotationReference(
      vehiclePlanItems(plan),
      vehicleQuotationItems(quotation),
      buildVehicleMatcher,
      buildVehicleMatcher
    )
  ) {
    invalidSections.push('Veículo');
  }

  if (
    !validatePlanQuotationReference(
      treatmentPlanItems(plan),
      treatmentQuotationItems(quotation),
      buildTreatmentMatcher,
      buildTreatmentMatcher
    )
  ) {
    invalidSections.push('Tratamento');
  }

  if (
    !validatePlanQuotationReference(
      destinationPlanItems(plan),
      destinationQuotationItems(quotation),
      buildDestinationMatcher,
      buildDestinationMatcher
    )
  ) {
    invalidSections.push('Destino final');
  }

  return invalidSections.map(
    (section) =>
      `${residuo.residuoCliente} (Plano: #${plan.idPropostaResiduoPlano} - Cotação: #${
        quotation.idPropostaResiduoPlanoCotacao || '-'
      } - ${section})`
  );
};

const validateResidueQuotationPricing = (
  residuo: ProposalResidueType,
  plan: ProposalResiduePlanType,
  quotation: ProposalResiduePlanQuotationType
) => {
  const badItems: string[] = [];

  for (const item of packagingQuotationItems(quotation)) {
    const refItem = packagingPlanItems(plan).find(
      (planItem) =>
        `${planItem.idPropostaResiduoPlanoAcondicionamento}` ===
        `${item.idPropostaResiduoPlanoAcondicionamento}`
    );

    if (!validateResidueQuotationItemPricing(item, refItem?.precificacao?.tipo)) {
      badItems.push(
        `${residuo.residuoCliente} (Plano: #${plan.idPropostaResiduoPlano} - Cotação: #${
          quotation.idPropostaResiduoPlanoCotacao || '-'
        } - Acondicionamento)`
      );
    }
  }

  for (const item of equipmentQuotationItems(quotation)) {
    const refItem = equipmentPlanItems(plan).find(
      (planItem) =>
        `${planItem.idPropostaResiduoPlanoEquipamento}` === `${item.idPropostaResiduoPlanoEquipamento}`
    );

    if (!validateResidueQuotationItemPricing(item, refItem?.precificacao?.tipo)) {
      badItems.push(
        `${residuo.residuoCliente} (Plano: #${plan.idPropostaResiduoPlano} - Cotação: #${
          quotation.idPropostaResiduoPlanoCotacao || '-'
        } - Equipamento)`
      );
    }
  }

  for (const item of vehicleQuotationItems(quotation)) {
    if (!validateResidueQuotationItemPricing(item, plan.veiculo?.precificacao?.tipo)) {
      badItems.push(
        `${residuo.residuoCliente} (Plano: #${plan.idPropostaResiduoPlano} - Cotação: #${
          quotation.idPropostaResiduoPlanoCotacao || '-'
        } - Veículo)`
      );
    }
  }

  for (const item of treatmentQuotationItems(quotation)) {
    if (!validateResidueQuotationItemPricing(item, plan.tratamento?.precificacao?.tipo)) {
      badItems.push(
        `${residuo.residuoCliente} (Plano: #${plan.idPropostaResiduoPlano} - Cotação: #${
          quotation.idPropostaResiduoPlanoCotacao || '-'
        } - Tratamento)`
      );
    }
  }

  for (const item of destinationQuotationItems(quotation)) {
    if (!validateResidueQuotationItemPricing(item, plan.destinoFinal?.precificacao?.tipo)) {
      badItems.push(
        `${residuo.residuoCliente} (Plano: #${plan.idPropostaResiduoPlano} - Cotação: #${
          quotation.idPropostaResiduoPlanoCotacao || '-'
        } - Destino final)`
      );
    }
  }

  return badItems;
};

const validateServiceQuotationPricing = (
  servico: ProposalServiceType,
  quotation: ProposalServiceQuotationType
) => hasQuotationCustomerPricing(quotation, servico.precificacao?.tipo);

const validateServiceQuotationReference = (
  servico: ProposalServiceType,
  quotation: ProposalServiceQuotationType
) => {
  if (!quotation.idFornecedor) {
    return true;
  }

  let serviceRef = 'F0';
  if (servico.idResiduoAcondicionamento) {
    serviceRef = `A${servico.idResiduoAcondicionamento}`;
  } else if (servico.idResiduoEquipamento) {
    serviceRef = `E${servico.idResiduoEquipamento}`;
  }

  let quotationRef = 'F0';
  if (quotation.idResiduoAcondicionamento) {
    quotationRef = `A${quotation.idResiduoAcondicionamento}`;
  } else if (quotation.idResiduoEquipamento) {
    quotationRef = `E${quotation.idResiduoEquipamento}`;
  }

  return serviceRef === quotationRef;
};

const makeValidationError = (message: string, list: string[]): ValidationResultType =>
  list.length > 0
    ? {
        isBusinessException: true,
        message,
        list,
      }
    : null;

const validarReferenciaServicoCompartilhado = (
  idResiduoAcondicionamento: number,
  idEntidadeEndereco: number,
  servico: ProposalServiceType | undefined
) =>
  !!servico &&
  servico.compartilhado &&
  servico.cotacoes.findIndex((y) => y.primario) > -1 &&
  servico.idResiduoAcondicionamento?.toString() === idResiduoAcondicionamento?.toString() &&
  servico.idEntidadeEndereco === idEntidadeEndereco;

const validarAnaliseFinanceira = (proposta: ProposalType): ValidationResultType => {
  const { residuos, servicos } = proposta;

  if (residuos.length === 0 && servicos.length === 0) {
    return {
      isBusinessException: true,
      message: 'A proposta deve possuir ao menos um resíduo ou serviço.',
      list: [],
    };
  }

  let badItems = residuos.filter((item) => item.planos.length === 0).map((item) => item.residuoCliente);
  let error = makeValidationError('O(s) resíduo(s) abaixo não possui(em) um plano:', badItems);
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    const valido = residuo.planos.some((plan) => hasCompletePlanDefinition(plan));
    if (!valido) {
      badItems.push(residuo.residuoCliente);
    }
  }
  error = makeValidationError('O(s) resíduo(s) abaixo possui(em) ao menos um plano inválido:', badItems);
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    const planosRegulares = residuo.planos.filter((plan) => plan.codigoPlanoTipo !== 'M');
    if (planosRegulares.length > 0 && !planosRegulares.some((plan) => plan.primario)) {
      badItems.push(residuo.residuoCliente);
    }
  }
  error = makeValidationError('O(s) resíduo(s) abaixo não possui(em) um plano primário regular:', badItems);
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    const planosRegularesPrimarios = residuo.planos.filter(
      (plan) => plan.codigoPlanoTipo !== 'M' && plan.primario
    );
    if (planosRegularesPrimarios.length > 1) {
      badItems.push(residuo.residuoCliente);
    }
  }
  error = makeValidationError(
    'O(s) resíduo(s) abaixo possui(em) mais de um plano primário regular:',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    for (const plano of residuo.planos) {
      for (const item of plano.acondicionamentos) {
        if (!hasPlanPricing(item)) {
          badItems.push(
            `${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano} - Acondicionamento)`
          );
        }
      }

      for (const item of plano.equipamentos) {
        if (!hasPlanPricing(item)) {
          badItems.push(`${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano} - Equipamento)`);
        }
      }

      if (plano.veiculo && !hasPlanPricing(plano.veiculo)) {
        badItems.push(`${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano} - Veículo)`);
      }

      if (plano.tratamento && !hasPlanPricing(plano.tratamento)) {
        badItems.push(`${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano} - Tratamento)`);
      }

      if (plano.destinoFinal && !hasPlanPricing(plano.destinoFinal)) {
        badItems.push(`${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano} - Destino final)`);
      }
    }
  }
  error = makeValidationError(
    'O(s) resíduo(s) abaixo utiliza(m) precificação geral, mas não possui(em) preço e imposto gerais no plano:',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    for (const plano of residuo.planos) {
      const { cotacoes } = plano;

      if (cotacoes.length === 0 || cotacoes.some((quotation) => !quotation.completo)) {
        badItems.push(`${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano})`);
      }
    }
  }
  error = makeValidationError(
    'O(s) plano(s) de coleta de resíduo não possui(em) cotação(ões) ou contém cotação(ões) incompletas:',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    for (const plano of residuo.planos) {
      if (plano.cotacoes.every((quotation) => !quotation.primario)) {
        badItems.push(`${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano})`);
      }
    }
  }
  error = makeValidationError(
    'O(s) plano(s) de coleta de resíduo não possui(em) uma cotação primária:',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    for (const plano of residuo.planos) {
      const cotacoesPrimarias = plano.cotacoes.filter((quotation) => quotation.primario);
      if (cotacoesPrimarias.length > 1) {
        badItems.push(`${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano})`);
      }
    }
  }
  error = makeValidationError(
    'O(s) plano(s) de coleta de resíduo possui(em) mais de uma cotação primária:',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    for (const plano of residuo.planos) {
      for (const quotation of plano.cotacoes) {
        badItems.push(...validateResidueQuotationReferences(residuo, plano, quotation));
      }
    }
  }
  error = makeValidationError(
    'O(s) plano(s) de coleta de resíduo possui(em) cotação(ões) com referência inválida:',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos) {
    for (const plano of residuo.planos) {
      for (const quotation of plano.cotacoes) {
        badItems.push(...validateResidueQuotationPricing(residuo, plano, quotation));
      }
    }
  }
  error = makeValidationError(
    'O(s) plano(s) de coleta de resíduo possui(em) cotação(ões) sem dados de precificação válidos:',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const residuo of residuos.filter((item) => item.compra)) {
    for (const plano of residuo.planos.filter(
      (item) => item.codigoPlanoTipo === ProposalResiduePlanCodeEnum.Regular
    )) {
      for (const cotacao of plano.cotacoes) {
        if (!cotacao.destinoFinal?.receita) {
          badItems.push(`${residuo.residuoCliente} (Plano: #${plano.idPropostaResiduoPlano})`);
        }
      }
    }
  }
  error = makeValidationError(
    'O(s) resíduo(s) de "Compra" precisa(m) ter um destino final de "Receita":',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const servico of servicos) {
    if (!isSpecificPricing(servico.precificacao?.tipo) && !hasGeneralPricing(servico.precificacao)) {
      badItems.push(servico.servico);
    }
  }
  error = makeValidationError(
    'O(s) serviço(s) abaixo utiliza(m) precificação geral, mas não possui(em) preço e imposto gerais:',
    badItems
  );
  if (error) {
    return error;
  }

  badItems = [];
  for (const servico of servicos) {
    if (servico.cotacoes.every((quotation) => !quotation.primario)) {
      badItems.push(servico.servico);
    }
  }
  error = makeValidationError('O(s) serviço(s) não possui(em) uma cotação primária:', badItems);
  if (error) {
    return error;
  }

  badItems = [];
  for (const servico of servicos) {
    const cotacoesPrimarias = servico.cotacoes.filter((item) => item.primario);
    if (cotacoesPrimarias.length > 1) {
      badItems.push(servico.servico);
    }
  }
  error = makeValidationError('O(s) serviço(s) possui(em) mais de uma cotação primária:', badItems);
  if (error) {
    return error;
  }

  badItems = [];
  for (const servico of servicos) {
    for (const quotation of servico.cotacoes) {
      if (!validateServiceQuotationReference(servico, quotation)) {
        badItems.push(servico.servico);
        break;
      }
    }
  }
  error = makeValidationError('O(s) serviço(s) possui(em) cotação(ões) de referência inválida:', badItems);
  if (error) {
    return error;
  }

  badItems = [];
  for (const servico of servicos) {
    for (const quotation of servico.cotacoes) {
      if (!validateServiceQuotationPricing(servico, quotation)) {
        badItems.push(servico.servico);
        break;
      }
    }
  }
  error = makeValidationError(
    'O(s) serviço(s) possui(em) cotação(ões) sem dados de precificação válidos:',
    badItems
  );
  if (error) {
    return error;
  }

  return null;
};

export { validarAnaliseFinanceira, validarReferenciaServicoCompartilhado };
